import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, ArrowRight, ChevronLeft, AlertCircle } from "lucide-react";
import { KD9LogoCircle, KD9LogoHorizontal, KD9Watermark } from "@/components/KD9Logo";
import ThemeToggle from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";

const AuthShell = ({ children, side = "right" }) => (
  <div
    className="relative min-h-screen flex"
    style={{ background: "var(--kd9-bg)" }}
    data-testid="auth-shell"
  >
    {/* decorative panel */}
    <div
      className={`hidden lg:flex relative w-1/2 overflow-hidden ${
        side === "left" ? "order-2" : "order-1"
      }`}
    >
      <img
        src="https://images.unsplash.com/photo-1539650116574-75c0c6d73f6e?auto=format&fit=crop&w=1600&q=80"
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-60"
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, var(--kd9-overlay) 0%, var(--kd9-overlay) 100%), radial-gradient(circle at 60% 40%, var(--kd9-glow), transparent 60%)",
        }}
      />
      <KD9Watermark size={760} className="-left-32 top-10" />
      <KD9Watermark size={520} className="right-0 bottom-0 kd9-pulse" />

      <div className="relative z-10 flex flex-col justify-between p-12 w-full">
        <KD9LogoHorizontal size={42} />
        <div>
          <div className="kd9-pulse mb-8">
            <KD9LogoCircle size={260} />
          </div>
          <h2
            className="font-cinzel kd9-gold-text text-4xl lg:text-5xl tracking-[0.06em] mb-4 leading-tight"
          >
            LA ENERGÍA QUE<br />DESPIERTA LO ANCESTRAL
          </h2>
          <p
            className="font-cormorant italic text-xl max-w-md"
            style={{ color: "var(--kd9-text-soft)" }}
          >
            &ldquo;No es casualidad que estés aquí. Es energía, es propósito, es tu camino.&rdquo;
          </p>
        </div>
        <div
          className="text-[11px] font-mont tracking-[0.4em] uppercase"
          style={{ color: "var(--kd9-gold)" }}
        >
          KAHOS DELTA 9 · KD9
        </div>
      </div>
    </div>

    <div
      className={`relative w-full lg:w-1/2 flex flex-col ${
        side === "left" ? "order-1" : "order-2"
      }`}
    >
      <KD9Watermark size={520} opacity={0.04} className="absolute -right-32 -bottom-20" />
      <div className="flex items-center justify-between p-6 lg:p-8 relative z-10">
        <Link
          to="/"
          className="flex items-center gap-2 text-sm font-mont tracking-wider hover:text-[color:var(--kd9-gold)] transition-colors"
          style={{ color: "var(--kd9-text-soft)" }}
          data-testid="auth-back-link"
        >
          <ChevronLeft size={16} /> Volver
        </Link>
        <ThemeToggle />
      </div>
      <div className="flex-1 flex items-center justify-center px-6 lg:px-12 pb-12 relative z-10">
        {children}
      </div>
    </div>
  </div>
);

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, blocked } = useAuth();
  const { t } = useLanguage();
  const [showPwd, setShowPwd] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (blocked) navigate("/cuenta-bloqueada", { replace: true });
  }, [blocked, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      const destination = location.state?.from;
      navigate(typeof destination === "string" && destination.startsWith("/app/") ? destination : "/app/inicio", { replace: true });
      return;
    }
    if (res.verificationRequired) {
      const destination = typeof location.state?.from === "string" && location.state.from.startsWith("/app/")
        ? location.state.from
        : "/app/inicio";
      const activation = {
        activationToken: res.activationToken,
        phoneMasked: res.phoneMasked,
        otpSent: false,
        destination,
      };
      window.sessionStorage.setItem("kd9-whatsapp-activation", JSON.stringify(activation));
      navigate("/verificar-whatsapp", { replace: true, state: activation });
      return;
    }
    setError(res.error || t("auth.login.error"));
  };

  return (
    <AuthShell>
      <div className="w-full max-w-md kd9-fade-up" data-testid="login-form-container">
        <div className="lg:hidden mb-10 flex justify-center">
          <KD9LogoCircle size={96} />
        </div>
        <h1 className="font-cinzel kd9-gold-text text-3xl md:text-4xl tracking-[0.08em] text-center mb-2">
          {t("auth.login.title")}
        </h1>
        <p
          className="font-cormorant italic text-lg text-center mb-10"
          style={{ color: "var(--kd9-text-soft)" }}
        >
          {t("auth.login.subtitle")}
        </p>

        <form onSubmit={onSubmit} className="space-y-5">
          {error && (
            <div
              className="flex items-start gap-2 px-4 py-3 rounded-lg border"
              style={{
                borderColor: "rgba(122,14,23,0.5)",
                background: "rgba(122,14,23,0.12)",
                color: "#FCA5A5",
              }}
              data-testid="login-error"
            >
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span className="text-sm font-mont">{error}</span>
            </div>
          )}

          <FieldGroup label={t("auth.login.email")} icon={Mail}>
            <input
              type="email"
              className="kd9-input"
              placeholder="tucorreo@kd9.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              data-testid="login-email-input"
            />
          </FieldGroup>

          <FieldGroup label={t("auth.login.password")} icon={Lock}>
            <input
              type={showPwd ? "text" : "password"}
              className="kd9-input pr-12"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              data-testid="login-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPwd(!showPwd)}
              className="absolute right-4 top-1/2 -translate-y-1/2"
              aria-label="Mostrar contraseña"
              data-testid="login-password-toggle"
            >
              {showPwd ? (
                <EyeOff size={16} style={{ color: "var(--kd9-text-soft)" }} />
              ) : (
                <Eye size={16} style={{ color: "var(--kd9-text-soft)" }} />
              )}
            </button>
          </FieldGroup>

          <div className="flex items-center justify-between text-sm">
            <label className="flex items-center gap-2 font-mont">
              <input
                type="checkbox"
                className="w-4 h-4 accent-[color:var(--kd9-gold)]"
                data-testid="login-remember"
              />
              <span style={{ color: "var(--kd9-text-soft)" }}>{t("auth.login.remember")}</span>
            </label>
            <Link
              to="#"
              className="font-mont"
              style={{ color: "var(--kd9-gold)" }}
              data-testid="login-forgot-link"
            >
              {t("auth.login.forgot")}
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="kd9-btn-gold w-full py-4 rounded-xl text-[12px] flex items-center justify-center gap-3 mt-2"
            style={{ opacity: loading ? 0.7 : 1, pointerEvents: loading ? "none" : "auto" }}
            data-testid="login-submit-btn"
          >
            {loading ? t("auth.login.loading") : t("auth.login.submit")} <ArrowRight size={16} />
          </button>

          <div className="text-center text-sm font-mont mt-6 pb-2" style={{ color: "var(--kd9-text-soft)" }}>
            ¿No tienes cuenta?{" "}
            <Link to="/register" className="font-semibold" style={{ color: "var(--kd9-gold)" }} data-testid="login-register-link">
              Crear cuenta
            </Link>
          </div>

        </form>
      </div>
    </AuthShell>
  );
};

export const FieldGroup = ({ label, icon: Icon, children }) => (
  <div>
    <label
      className="font-mont text-[11px] tracking-[0.25em] uppercase mb-2 block"
      style={{ color: "var(--kd9-text-soft)" }}
    >
      {label}
    </label>
    <div className="relative">
      {Icon && (
        <Icon
          size={16}
          className="absolute left-4 top-1/2 -translate-y-1/2"
          style={{ color: "var(--kd9-gold)" }}
        />
      )}
      {children}
    </div>
  </div>
);

export { AuthShell };
export default Login;
